import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IFormDocument } from 'app/services/api5-service/api.interface';
import { IFormButtonUi } from 'components/form-button/model';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { filterDefined } from 'utils/is-defined';

import { IFormModel } from './dynamic-form.interface';
import { DynamicFormService } from './dynamic-form.service';
import { DYNAMIC_FORM_DOC } from './dynamic-form.utils';
import {
  ISPFieldType,
  DynamicFormContext,
  ISPFieldConfig,
  VisibilityFunctionType,
  ISPFormState,
} from './model';
import { ButtonsService } from './services/buttons.service';
import { CaptchaService } from './services/captcha.service';
import { ConditionService } from './services/condition.service';
import { DisabledService } from './services/disabled.service';
import { FontService } from './services/font.service';
import { FormService, FORM_DOC } from './services/form.service';
import { HiddenService } from './services/hidden.service';
import { LayoutService } from './services/layout.service';
import { ListService } from './services/list.service';
import { LoaderService } from './services/loader/loader.service';
import { MixedService } from './services/mixed-service';
import { SelectService } from './services/select.service';
import { SetValuesService } from './services/set-values.service';
import { TriggerService } from './services/trigger/trigger.service';
import { ValidationService } from './services/validation.service';
import { VisibilityService } from './services/visibility.service';
import { TemplateConfig } from './types/template/model';
import { DynamicFormTemplateDirective } from './types/template/template.directive';
import { TemplateService } from './types/template/template.service';
import { FIELD_HIDDEN_CLASS } from './utils/class-fields';
import { mergeConfigs } from './utils/formly-configs';

/**
 * Field list component
 *
 * Usage:
 * ```html
 * <isp-dynamic-form [doc]="doc$ | async"></isp-dynamic-form>
 * ```
 */
@UntilDestroy()
@Component({
  selector: 'isp-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./scss/dynamic-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DynamicFormService,
    FormService,
    SetValuesService,
    ValidationService,
    ConditionService,
    DisabledService,
    HiddenService,
    ButtonsService,
    MixedService,
    ListService,
    SelectService,
    LayoutService,
    CaptchaService,
    FontService,
    VisibilityService,
    TemplateService,
    LoaderService,
    TriggerService,
    {
      provide: FORM_DOC,
      useExisting: FormService,
    },
    {
      provide: DYNAMIC_FORM_DOC,
      useExisting: DynamicFormService,
    },
  ],
})
export class DynamicFormComponent implements OnInit, AfterViewInit {
  private readonly doc$: BehaviorSubject<IFormDocument | null> =
    new BehaviorSubject(null);

  readonly footerButtons$ = this.buttonsService.footerButtons$;

  readonly isHiddenFooterButtons$ = this.buttonsService.isHiddenFooterButtons$;

  readonly formGroup$ = this.formService.formGroup$;

  get formGroup(): UntypedFormGroup {
    return this.formService.formGroup;
  }

  readonly configs$ = this.formService.configs$;

  readonly model$ = this.formService.model$;

  readonly options$ = this.formService.options$;

  readonly formId = this.layoutService.formId;

  /** Set form model */
  @Input() set model(model: IFormModel | null) {
    this.formService.setModel(model || {});
  }

  /** Get form model. @WARN use outside of component */
  get model(): IFormModel {
    return this.formService.model;
  }

  /** Context information for dynamic-form */
  @Input() set context(context: DynamicFormContext) {
    this.dynamicFormService.context = context;
  }

  @Input() set dropdownParentSelector(element: string) {
    this.dynamicFormService.dropdownParentSelector = element;
  }

  @Input() set isExtformLayout(value: boolean) {
    this.dynamicFormService.isExtformLayout = value;
  }

  @Input() set dropdownScrollSelector(selector: string) {
    this.dynamicFormService.dropdownScrollSelector = selector;
  }

  @Input() set showHints(canShow: boolean) {
    this.dynamicFormService.showHints = canShow;
  }

  /** Dropdown list width. It cannot be setted by css, cause dropdown opens out of dynamic-form css scope */
  @Input() set selectDropdownWidth(width: string) {
    this.dynamicFormService.selectDropdownWidth = width;
  }

  @Input() set fakeLabels(fakeLabels: boolean) {
    this.dynamicFormService.fakeLabels = fakeLabels;
  }

  /** Document instance */
  @Input() set doc(doc: IFormDocument | null) {
    this.doc$.next(doc);
  }

  @Input() showFooter = true;

  /** Should general error be displayed in form layout */
  @Input() set showGeneralErrorInNotification(display: boolean) {
    this.dynamicFormService.showGeneralErrorInNotification = display;
  }

  get showGeneralErrorInNotification(): boolean {
    return this.dynamicFormService.showGeneralErrorInNotification;
  }

  /**
   * Flag to control the synchronization of the model with doc
   *
   * @TODO e.polyakov for the branding form, there is no need to synchronize with doc, remove this after BILLDr-543
   */
  @Input() set needSyncModelWithDoc(needSync: boolean) {
    this.dynamicFormService.needSyncModelWithDoc = needSync;
  }

  /** Function by which the visible fields will be determined */
  @Input() set visibilityFunc(visibilityFunction: VisibilityFunctionType) {
    this.visibilityService.visibilityFunc = visibilityFunction;
  }

  /** A function that allows you to additionally process the generated field configs */
  @Input() set handlingConfigsFunc(
    handlingFunction: (configs: ISPFieldConfig[], state: ISPFormState) => void,
  ) {
    this.dynamicFormService.customHandlingConfigsFunction = handlingFunction;
  }

  @Input() showBlockingPreloader = true;

  /** Submit form by Enter key press */
  @Input() submittableOnEnter = true;

  /** Collapsible's toggle event */
  @Output() readonly collapseToggle = this.dynamicFormService.collapseEvent;

  /** Button click event */
  @Output() readonly buttonClick = this.dynamicFormService.buttonClickEvent;

  /** Toolbar click event */
  @Output() readonly toolbarClick = this.dynamicFormService.toolbarClickEvent;

  /** Wizard click event */
  @Output() readonly wizardClick = this.dynamicFormService.wizardClickEvent;

  /** List select item event */
  @Output() readonly listSelect = this.dynamicFormService.listSelectEvent;

  /** Link click event */
  @Output() readonly linkClick = this.dynamicFormService.linkClickEvent;

  /** Reset or update field value from chips event */
  @Output() readonly chipsEvent = this.dynamicFormService.chipsEvent;

  /** Model change event */
  @Output() readonly changeModel: EventEmitter<IFormModel> = new EventEmitter();

  /** Templates for dynamic-form */
  @ContentChildren(DynamicFormTemplateDirective)
  contentTemplates: QueryList<DynamicFormTemplateDirective>;

  /** Templates for dynamic-form */
  @ViewChildren(DynamicFormTemplateDirective)
  viewTemplates: QueryList<DynamicFormTemplateDirective>;

  @Input() templatesConfigs: TemplateConfig[];

  readonly contentTemplatesConfigs: TemplateConfig[] = [
    {
      type: ISPFieldType.Template,
      props: {
        templateName: 'buttons',
        layoutPlace: 'footer',
        isHidden: true,
        isHidden$: this.isHiddenFooterButtons$.pipe(
          map(isButtonsHidden => !this.showFooter || isButtonsHidden),
        ),
      },
      expressions: {
        'props.isHidden': this.isHiddenFooterButtons$.pipe(
          map(isButtonsHidden => !this.showFooter || isButtonsHidden),
          distinctUntilChanged(),
        ),
        className: this.isHiddenFooterButtons$.pipe(
          map(isButtonsHidden => !this.showFooter || isButtonsHidden),
          map(isHidden => (isHidden ? FIELD_HIDDEN_CLASS : '')),
          distinctUntilChanged(),
        ),
      },
    },
  ];

  constructor(
    private readonly formService: FormService,
    private readonly layoutService: LayoutService,
    private readonly buttonsService: ButtonsService,
    private readonly dynamicFormService: DynamicFormService,
    private readonly visibilityService: VisibilityService,
    private readonly templateService: TemplateService,
    private readonly hiddenService: HiddenService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  private getTemplateConfigs(): TemplateConfig[] {
    const viewTemplates = this.templatesConfigs || [];
    const contentTemplates = this.contentTemplatesConfigs;

    const templatesMap = new Map<string, TemplateConfig>();
    contentTemplates.forEach(config => {
      templatesMap.set(config.props.templateName, config);
    });
    viewTemplates.forEach(config => {
      if (
        templatesMap.has(config.props.templateName) &&
        !config.props.replacedConfig
      ) {
        const existedConfig = templatesMap.get(config.props.templateName)!;
        templatesMap.set(
          config.props.templateName,
          mergeConfigs(existedConfig, config),
        );
      } else {
        templatesMap.set(config.props.templateName, config);
      }
    });

    return Array.from(templatesMap.values());
  }

  ngOnInit(): void {
    this.formGroup$
      .pipe(
        switchMap(group => group.valueChanges),
        untilDestroyed(this),
      )
      .subscribe(model => {
        this.changeModel.emit(model);
      });

    this.doc$.pipe(filterDefined(), untilDestroyed(this)).subscribe(doc => {
      const templates = this.getTemplateConfigs();
      this.dynamicFormService.setNewDoc(doc, templates);
    });
  }

  ngAfterViewInit(): void {
    const contentTemplates = Array.from(this.contentTemplates || []);
    const viewTemplates = Array.from(this.viewTemplates || []);
    const templates = [...viewTemplates, ...contentTemplates];
    this.templateService.register(templates);
    this.cdr.detectChanges();
  }

  /**
   * Emits the form button click event and form's value
   *
   * @param button - clicked button
   */
  emitButtonClick(button: IFormButtonUi): void {
    this.dynamicFormService.emitButtonClick(button);
  }

  /**
   * Sends the keyboard event to submit
   *
   * @param event - keyboard "Enter" key press event
   */
  submitFromKeyboard(event: KeyboardEvent): void {
    if (this.submittableOnEnter) {
      this.dynamicFormService.submitFromKeyboard(event);
    }
  }

  /**
   * Clear the form
   */
  clearForm(): void {
    this.formService.clearForm();
  }

  /**
   * Reset form state
   */
  resetForm(): void {
    this.formService.resetForm();
  }

  /**
   * Filter not hiddens footer buttons
   *
   * @param buttonList - Button FE data
   */
  getVisibleFooterButtons(buttonList: IFormButtonUi[]): IFormButtonUi[] {
    return buttonList.filter(
      button => !this.hiddenService.isHidden(button.name),
    );
  }
}
